/**
 * Blacklist — every blacklisted account, over all time: what it deposited,
 * what issuers refused, the unlisted accounts sharing a card or device with
 * it, and its fraud ring. Data: GET /analytics/tt/blacklist.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Ban, CircleCheck, Fingerprint, Link2, TriangleAlert } from "lucide-react";
import { useTTQuery } from "../ttApi";
import { count, dateOnly, dateTime, money } from "../ttFormat";
import {
  AccountLinks,
  INK,
  LoadState,
  Note,
  Pager,
  ReviewCard,
  SearchBox,
  TONE,
  Table,
  Tile,
  Tiles,
  linkButton,
} from "../ttKit";

const PAGE_SIZE = 25;

export default function BlacklistPanel({ fresh, openAccount, openRing }) {
  const state = useTTQuery("blacklist", { fresh });
  return (
    <LoadState state={state} height={320}>
      {(d) => <BlacklistBody d={d} openAccount={openAccount} openRing={openRing} />}
    </LoadState>
  );
}

function BlacklistBody({ d, openAccount, openRing }) {
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  useEffect(() => setPage(1), [q]);

  const rows = useMemo(() => {
    const ql = q.toLowerCase();
    if (!ql) return d.rows;
    return d.rows.filter(
      (r) => r.email.includes(ql) || (r.ring || "").includes(ql) || r.linked_unlisted.some((e) => e.includes(ql))
    );
  }, [d.rows, q]);
  const k = d.kpis;

  return (
    <>
      <Tiles>
        <Tile tone="red" icon={Ban} label="Blacklisted accounts" value={count(k.accounts)} sub="Current list" />
        <Tile tone="red" icon={Fingerprint} label="Blacklisted devices" value={count(k.devices)} sub="Current list" />
        <Tile
          tone="green"
          icon={CircleCheck}
          label="Completed"
          value={money(k.completed_usd)}
          sub="Every deposit the blacklisted accounts completed"
        />
        <Tile
          tone="red"
          icon={TriangleAlert}
          label="Refused by issuers"
          value={money(k.fraud_decline_usd)}
          sub={`${count(k.fraud_declines)} fraud declines`}
        />
        <Tile
          tone="amber"
          icon={Link2}
          label="Linked, not listed"
          value={count(k.linked_unlisted)}
          sub="Share a card or device with a blacklisted account"
        />
      </Tiles>

      <ReviewCard
        eyebrow="Blacklist"
        title="Every blacklisted account"
        desc="All time, most refused first. Linked, not listed are the accounts sharing a card or device with this one that are not on the list. Click a row to open the account."
        right={<SearchBox value={q} onChange={setQ} placeholder="Search account or ring" />}
      >
        <Table
          emptyState="No blacklisted accounts match."
          columns={COLUMNS(openAccount, openRing)}
          rows={rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE).map((r) => ({
            key: r.email,
            ...r,
            // An entry for an email that never deposited has no account page.
            onClick: r.attempts ? () => openAccount(r.email) : undefined,
          }))}
        />
        <Pager page={page} limit={PAGE_SIZE} total={rows.length} onPage={setPage} />
        <Note style={{ marginTop: 10 }}>
          Totals use each account's full history{d.as_of ? `, as of ${dateTime(d.as_of)}` : ""}. Listed is the date of
          the account's latest blacklist entry.
        </Note>
      </ReviewCard>
    </>
  );
}

const COLUMNS = (openAccount, openRing) => [
  { key: "email", label: "Account", render: (r) => <span style={{ color: TONE.blue.text }}>{r.email}</span> },
  {
    key: "listed_since",
    label: "Listed",
    render: (r) => (
      <div style={{ padding: "4px 0" }}>
        <div>{dateOnly(r.listed_since)}</div>
        {r.listed_by && <div style={{ fontSize: 11.5, color: INK.muted }}>{r.listed_by}</div>}
      </div>
    ),
  },
  { key: "attempts", label: "Attempts", numeric: true, render: (r) => count(r.attempts) },
  { key: "completed_usd", label: "Completed", numeric: true, render: (r) => money(r.completed_usd) },
  {
    key: "fraud_decline_usd",
    label: "Refused",
    numeric: true,
    render: (r) => (r.fraud_declines ? `${count(r.fraud_declines)} · ${money(r.fraud_decline_usd)}` : "0"),
  },
  { key: "cards", label: "Cards", numeric: true, render: (r) => count(r.cards) },
  {
    key: "linked_unlisted",
    label: "Linked, not listed",
    render: (r) => <AccountLinks emails={r.linked_unlisted} onOpen={openAccount} separator=", " max={320} />,
  },
  {
    key: "ring",
    label: "Ring",
    render: (r) =>
      r.ring ? (
        <div style={{ padding: "4px 0" }}>
          <button
            type="button"
            style={linkButton}
            onClick={(ev) => {
              ev.stopPropagation();
              openRing(r.ring);
            }}
          >
            {r.ring}
          </button>
          <div style={{ fontSize: 11.5, color: INK.muted }}>{r.ring_grade}</div>
        </div>
      ) : (
        "—"
      ),
  },
];
